// SHARED Google Maps JS loader config + runtime error detector.
//
// The @googlemaps/js-api-loader singleton throws "Loader must not be called
// again with different options" if two components pass different `libraries`
// arrays. Every consumer MUST import this constant so the loader is
// initialised identically across MapView, MapPointPicker, and
// LocationAutocomplete.
//
// NOTE: `libraries` is ADDITIVE — including 'places' still loads maps,
// so a single ['places'] value is sufficient for all our needs.
export const GOOGLE_MAPS_LIBRARIES = ['places'];

import { useEffect, useState } from 'react';

/**
 * useGoogleMapsRuntimeError()
 *
 * Detects the "This page can't load Google Maps correctly" modal that
 * Google injects at runtime when the Cloud project has billing disabled
 * OR the API key referrer restrictions don't include the current origin.
 *
 * The `loadError` returned by useJsApiLoader ONLY covers script-load
 * failures. Billing/referrer errors happen AFTER the script boots, so
 * loadError stays null — we have to sniff the DOM.
 *
 * The current Maps JS build injects a class-less <div> with inline styles
 * containing "Roboto" font-family. We watch for that + the .dismissButton
 * node Google always inserts alongside its error card.
 *
 * On detection we (a) flip errored=true so consumers can render a
 * fallback and NOT mount <GoogleMap>, and (b) actively REMOVE the injected
 * dialog from the DOM so it can't cover the app UI even if it was
 * injected by a widget outside our control (e.g. Places Autocomplete).
 *
 * Uses a MutationObserver for the component lifetime so late injections
 * (typing in an Autocomplete field, panning, etc.) are caught too.
 */
export function useGoogleMapsRuntimeError(isLoaded) {
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const isErrorNode = (el) => {
      if (!el || el.nodeType !== 1) return false;
      const style = (el.getAttribute && el.getAttribute('style')) || '';
      const text = el.textContent || '';
      return (
        /Roboto/i.test(style) &&
        /can'?t load Google Maps|BillingNotEnabled|do you own this website/i.test(text)
      );
    };

    const scrubAndFlag = () => {
      let found = false;
      // Sentinel 1: Google's dismissButton (present next to the dialog).
      document.querySelectorAll('.dismissButton').forEach((el) => {
        found = true;
        try { el.parentNode && el.parentNode.remove(); } catch { /* ignore */ }
      });
      // Sentinel 2: class-less injected dialog with Roboto inline styles.
      document.querySelectorAll('div[style*="Roboto"]').forEach((el) => {
        if (isErrorNode(el)) {
          found = true;
          try { el.remove(); } catch { /* ignore */ }
        }
      });
      // Sentinel 3: legacy .gm-err-container (older Maps JS builds).
      document.querySelectorAll('.gm-err-container').forEach((el) => {
        found = true;
        try { el.remove(); } catch { /* ignore */ }
      });
      if (found) setErrored(true);
    };

    scrubAndFlag();
    // Watch the whole page for late injections (Places Autocomplete throws
    // its dialog into document.body only after the first API request).
    const mo = new MutationObserver(() => scrubAndFlag());
    mo.observe(document.body, { childList: true, subtree: true });
    // Extra safety nets for the first few seconds after mount.
    const timers = [800, 2000, 4000].map((ms) => setTimeout(scrubAndFlag, ms));
    return () => {
      mo.disconnect();
      timers.forEach(clearTimeout);
    };
  }, [isLoaded]);
  return errored;
}
